<template>
    <div>
      <!-- Loading Spinner -->
      <b-row align-h="center" v-if="dataLoading">
        <b-spinner
          class="mt-4"
          style="width: 4rem; height: 4rem"
          type="grow"
          variant="primary"
          label="Spinning"
        ></b-spinner>
      </b-row>
      
      <div v-else>
        <!-- Source Configuration Card -->
        <b-card class="mb-2">
        <b-card-header class="pb-1">
          <b-card-title class="h5 mb-0">
            <feather-icon icon="CopyIcon" class="mr-1" />
            Source Configuration
          </b-card-title>
        </b-card-header>
        
        <b-card-body>
          <b-row>
            <!-- Source Campus -->
            <b-col md="6" sm="12" class="mb-2">
              <b-form-group label="Campus" label-class="font-weight-bolder text-dark" label-for="sourceCampus">
                <v-select
                  v-model="formData.sourceCampusID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="campuses"
                  :reduce="option => option.id"
                  label="branch"
                  :clearable="false"
                  placeholder="Select campus"
                  @input="onSourceCampusChange"
                  :loading="campusesLoading"
                />
              </b-form-group>
            </b-col>
  
            <!-- Source Classes (multiselect with All classes) -->
            <b-col md="6" sm="12" class="mb-2">
              <b-form-group label="Classes" label-class="font-weight-bolder text-dark" label-for="sourceClasses">
                <v-select
                  v-model="formData.sourceClassIDs"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="sourceClassOptions"
                  :reduce="option => option.id"
                  label="name"
                  :clearable="false"
                  :multiple="true"
                  placeholder="Select classes"
                  @input="onSourceClassesChange"
                  :loading="sourceClassesLoading"
                  :disabled="!formData.sourceCampusID || sourceClassesLoading"
                />
              </b-form-group>
            </b-col>
  
            
  
            <!-- Source Subject -->
            <b-col md="6" sm="12" class="mb-2">
              <b-form-group label="Subject" label-class="font-weight-bolder text-dark" label-for="sourceSubject">
                <v-select
                  v-model="formData.sourceSubjectID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="sourceSubjects"
                  :reduce="option => option.id"
                  label="subject"
                  :clearable="false"
                  placeholder="Select subject"
                  :loading="sourceSubjectsLoading"
                  :disabled="!formData.sourceCampusID || sourceSubjectsLoading"
                />
              </b-form-group>
            </b-col>

            <!-- Source Session -->
            <b-col md="6" sm="12" class="mb-2">
              <b-form-group label="Session *" label-class="font-weight-bolder text-dark" label-for="sourceSession">
                <v-select
                  v-model="formData.sourceSessionID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="sourceSessions"
                  :reduce="option => option.id"
                  label="session"
                  :clearable="false"
                  placeholder="Select session"
                  :loading="sourceSessionsLoading"
                  :disabled="!formData.sourceCampusID || sourceSessionsLoading"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
  
      <!-- Destination Campuses (multiselect) -->
      <b-card class="mb-2">
        <b-card-header class="pb-1">
            <b-card-title class="h5 mb-0">
              <feather-icon icon="ArrowRightIcon" class="mr-1" />
            Destination Campuses
            </b-card-title>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="6" sm="12" class="mb-2">
              <b-form-group label="Campuses" label-class="font-weight-bolder text-dark" label-for="destCampuses">
                  <v-select
                  v-model="formData.copyToCampusIDs"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="destinationCampusOptions"
                    :reduce="option => option.id"
                    label="branch"
                    :clearable="false"
                  :multiple="true"
                  placeholder="Select campuses"
                  @input="onCopyToCampusesChange"
                    :loading="campusesLoading"
                  :disabled="!formData.sourceCampusID || sourceClassesLoading || sourceSubjectsLoading"
                  />
                </b-form-group>
              </b-col>

              <!-- Destination Session -->
              <b-col md="6" sm="12" class="mb-2">
                <b-form-group label="Session *" label-class="font-weight-bolder text-dark" label-for="destinationSession">
                  <v-select
                    v-model="formData.destinationSessionID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="destinationSessions"
                    :reduce="option => option.id"
                    label="session"
                    :clearable="false"
                    placeholder="Select session"
                    :loading="destinationSessionsLoading"
                    :disabled="!formData.copyToCampusIDs.length || destinationSessionsLoading"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
  
      <!-- Action Buttons -->
      <div class="d-flex justify-content-center">
        <b-button
          variant="secondary"
          class="mr-2 px-4"
          @click="goBack"
          :disabled="saving"
        >
          Cancel
        </b-button>
        <b-button
          type="submit"
          variant="success"
          class="px-4"
          @click="saveData"
          :disabled="saving || !isFormValid"
        >
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          <feather-icon icon="SaveIcon" class="mr-1" v-else />
          {{ isEdit ? 'Update' : 'Save' }}
        </b-button>
      </div>
      </div>
    </div>
  </template>
  
  <script>
  import {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BFormGroup,
    BRow,
    BCol,
    BButton,
    BSpinner,
  } from "bootstrap-vue";
  import vSelect from "vue-select";
  import axios from "axios";
  import { mapActions } from "vuex";
  
  export default {
    components: {
      BCard,
      BCardHeader,
      BCardTitle,
      BCardBody,
      BFormGroup,
      BRow,
      BCol,
      BButton,
      BSpinner,
      vSelect,
    },
    data() {
      return {
        rights: {},
        isEdit: false,
        saving: false,
        dataLoading: false,
        campusesLoading: false,
        sourceClassesLoading: false,
        sourceSubjectsLoading: false,
        sourceSessionsLoading: false,
        destinationSessionsLoading: false,

        formData: {
          id: 0,
          sourceCampusID: null,
          sourceClassIDs: [],
          sourceSubjectID: null,
          sourceSessionID: null,
          destinationSessionID: null,
          copyToCampusIDs: [],
        },

        // Dropdown data
        campuses: [],
        sourceClasses: [],
        sourceSubjects: [],
        sourceSessions: [],
        destinationSessions: [],
      };
    },
    computed: {
      reportDomain() {
        return (this.$store.state.report || "").replace(/_/g, "");
      },
      plannerCampusID() {
        return this.reportDomain === "myskooliuss"
          ? 1
          : this.$store.state.userData.cId;
      },
      isFormValid() {
        return (
          this.formData.sourceCampusID !== null &&
          this.formData.sourceCampusID !== undefined &&
          Array.isArray(this.formData.sourceClassIDs) && this.formData.sourceClassIDs.length > 0 &&
          this.formData.sourceSubjectID !== null &&
          this.formData.sourceSubjectID !== undefined &&
          this.formData.sourceSessionID !== null &&
          this.formData.sourceSessionID !== undefined &&
          Array.isArray(this.formData.copyToCampusIDs) && this.formData.copyToCampusIDs.length > 0 &&
          this.formData.destinationSessionID !== null &&
          this.formData.destinationSessionID !== undefined
        );
      },
      // Build source class options with an All option
      sourceClassOptions() {
        const options = this.sourceClasses ? [...this.sourceClasses] : [];
        return [{ id: 'ALL', name: 'All classes' }, ...options];
      },
      // Build destination campus options excluding selected source campus, with All option
      destinationCampusOptions() {
        const options = (this.campuses || []).filter(c => c.id !== this.formData.sourceCampusID);
        return [{ id: 'ALL', branch: 'All campuses' }, ...options];
      },
    },
    async created() {
      // Initialize rights from store
      let right = {};
      this.$store.state.menu.forEach((el) => {
        el.children.forEach((ch) => {
          right[ch.route] = {
            view: ch.view,
            add: ch.add,
            edit: ch.edit,
            delete: ch.delete,
          };
        });
      });
      this.$store.commit("setRights", right);
  
      // Check for rights under the parent route name (replicate-planner) instead of the form route name
      const parentRouteName = 'replicate-planner';
      console.log('=== RIGHTS DEBUG ===');
      console.log('Current route name:', this.$route.name);
      console.log('Checking rights for parent route:', parentRouteName);
      console.log('Available rights in store:', this.$store.state.rights);
      console.log('Rights for parent route:', this.$store.state.rights[parentRouteName]);
      
      if (!this.$store.state.rights[parentRouteName]) {
        console.log('No rights found for parent route, redirecting to unauthorized');
        this.$router.replace({
          name: "misc-not-authorized",
        });
        return;
      }
      this.rights = this.$store.state.rights[parentRouteName];
      console.log('Assigned rights from parent route:', this.rights);
      
      console.log("Route params:", this.$route.params);
      console.log("Route param id:", this.$route.params.id);
      console.log("Route param id type:", typeof this.$route.params.id);
      
      // Check if this is edit mode by checking if id is not "0"
      this.isEdit = this.$route.params.id !== "0" && this.$route.params.id !== 0;
      console.log("Is edit mode:", this.isEdit);
      
      // Check rights for add/edit mode
      console.log('Mode check - isEdit:', this.isEdit, 'rights.edit:', this.rights.edit, 'rights.add:', this.rights.add);
      
      if (this.isEdit && !this.rights.edit) {
        console.log('No edit rights, redirecting to unauthorized');
        this.$router.replace({
          name: "misc-not-authorized",
        });
        return;
      } else if (!this.isEdit && !this.rights.add) {
        console.log('No add rights, redirecting to unauthorized');
        this.$router.replace({
          name: "misc-not-authorized",
        });
        return;
      }
      
      console.log('Rights check passed - proceeding with form');
      
      this.dataLoading = true;
      try {
        await this.loadInitialData();
        if (this.isEdit && this.$route.params.id) {
          console.log("Loading edit data for ID:", this.$route.params.id);
          await this.loadEditData();
        } else {
          console.log("This is add mode, not loading edit data");
          // Initialize form for add mode
          this.formData = {
            id: 0,
            sourceCampusID: null,
            sourceClassIDs: [],
            sourceSubjectID: null,
            sourceSessionID: null,
            destinationSessionID: null,
            copyToCampusIDs: [],
          };
        }
      } finally {
        this.dataLoading = false;
      }
    },
    methods: {
      ...mapActions({ get: "get", post: "post", put: "put" }),
      async loadInitialData() {
        await this.loadCampuses();
      },
  
      async loadSessionsForCampus(campusID) {
        if (!campusID) return [];

        const sessionsCampusID =
          this.reportDomain === "myskooliuss" ? 1 : campusID;
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${sessionsCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data?.status === "success" && Array.isArray(res.data.data)) {
          return res.data.data;
        }
        if (Array.isArray(res.data?.data)) {
          return res.data.data;
        }
        if (Array.isArray(res.data)) {
          return res.data;
        }
        return [];
      },

      getDefaultSessionID(sessions) {
        if (!sessions || !sessions.length) return null;
        const currentSession = sessions.find(
          (el) => el.isCurrentSession === true
        );
        return currentSession
          ? currentSession.id
          : sessions[sessions.length - 1].id;
      },

      async loadSourceSessions() {
        if (!this.formData.sourceCampusID) {
          this.sourceSessions = [];
          this.formData.sourceSessionID = null;
          return;
        }

        this.sourceSessionsLoading = true;
        try {
          this.sourceSessions = await this.loadSessionsForCampus(
            this.formData.sourceCampusID
          );
          if (!this.isEdit || !this.formData.sourceSessionID) {
            this.formData.sourceSessionID = this.getDefaultSessionID(
              this.sourceSessions
            );
          }
        } catch (error) {
          console.error("Error loading source sessions:", error);
          this.sourceSessions = [];
          this.formData.sourceSessionID = null;
        } finally {
          this.sourceSessionsLoading = false;
        }
      },

      async loadDestinationSessions() {
        const campusID = this.formData.copyToCampusIDs[0];
        if (!campusID) {
          this.destinationSessions = [];
          this.formData.destinationSessionID = null;
          return;
        }

        this.destinationSessionsLoading = true;
        try {
          this.destinationSessions = await this.loadSessionsForCampus(campusID);
          if (!this.isEdit || !this.formData.destinationSessionID) {
            this.formData.destinationSessionID = this.getDefaultSessionID(
              this.destinationSessions
            );
          }
        } catch (error) {
          console.error("Error loading destination sessions:", error);
          this.destinationSessions = [];
          this.formData.destinationSessionID = null;
        } finally {
          this.destinationSessionsLoading = false;
        }
      },

      async loadCampuses() {
        this.campusesLoading = true;
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "campuses?db=" +
              this.$store.state.userData.db +
              "&cId=" +
              this.$store.state.userData.cId,
            token: this.$store.state.userData.token,
          };
          this.campuses = await this.get(obj);
        } catch (error) {
          console.error("Error loading campuses:", error);
          this.$bvToast.toast("Error loading campuses", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.campusesLoading = false;
        }
      },
  
      async loadSourceClasses() {
        if (!this.formData.sourceCampusID) return;
        
        this.sourceClassesLoading = true;
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "myclasses/LoadClasses?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.formData.sourceCampusID +
              "&dID=0",
            token: this.$store.state.userData.token,
          };
          const response = await this.get(obj);
          this.sourceClasses = response.map((el) => el.cls);
        } catch (error) {
          console.error("Error loading source classes:", error);
        } finally {
          this.sourceClassesLoading = false;
        }
      },
  
      async loadSourceSubjects() {
        if (!this.formData.sourceCampusID) return;
        
        this.sourceSubjectsLoading = true;
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "Subjects?db=" +
              this.$store.state.userData.db +
              "&cId=" +
              this.formData.sourceCampusID,
            token: this.$store.state.userData.token,
          };
          this.sourceSubjects = await this.get(obj);
        } catch (error) {
          console.error("Error loading source subjects:", error);
        } finally {
          this.sourceSubjectsLoading = false;
        }
      },
  
      
  
      async loadEditData() {
        try {
          const url = this.$store.state.domain +
            "PlannerReplication/LoadSelected?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&id=" +
            this.$route.params.id;
            
          console.log("API URL:", url);
          console.log("User data:", this.$store.state.userData);
          
          const obj = {
            url: url,
            token: this.$store.state.userData.token,
            returnData: true, // Get the raw response data
          };
          const response = await this.get(obj);
          console.log("Edit data response:", response);
          
          let data = null;
          
          console.log("Raw response:", response);
          console.log("Response type:", typeof response);
          console.log("Is array:", Array.isArray(response));
          
          // Handle different response structures
          if (response && response.status === "success" && response.data && response.data.length > 0) {
            // Response has status and data structure
            data = response.data[0];
            console.log("Using status.data structure");
          } else if (response && Array.isArray(response) && response.length > 0) {
            // Response is a direct array
            data = response[0];
            console.log("Using direct array structure");
          } else if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
            // Response has data array without status
            data = response.data[0];
            console.log("Using data array structure");
          } else if (response && typeof response === 'object' && response.id) {
            // Response is a direct object
            data = response;
            console.log("Using direct object structure");
          }
          
          if (data) {
            console.log("Extracted data:", data);
            
            // Map response into new model
            this.formData = {
              id: data.id,
              sourceCampusID: data.sourceCampusID,
              sourceClassIDs: data.sourceClassID ? [data.sourceClassID] : [],
              sourceSubjectID: data.sourceSubjectID,
              sourceSessionID: data.sourceSessionID || null,
              destinationSessionID: data.destinationSessionID || null,
              copyToCampusIDs: data.destinationCampusID ? [data.destinationCampusID] : [],
            };
  
            console.log("Form data set:", this.formData);
  
            // Load dependent data sequentially
            if (this.formData.sourceCampusID) {
              console.log("Loading source classes for campus:", this.formData.sourceCampusID);
              await this.loadSourceClasses();
              console.log("Loading source subjects for campus:", this.formData.sourceCampusID);
              await this.loadSourceSubjects();
              await this.loadSourceSessions();
            }

            if (this.formData.copyToCampusIDs.length) {
              await this.loadDestinationSessions();
            }
  
            // Force Vue to update the form
            this.$nextTick(() => {
              console.log("Form data after loading:", this.formData);
              console.log("Source classes loaded:", this.sourceClasses.length);
              console.log("Source subjects loaded:", this.sourceSubjects.length);
            });
  
          } else {
            console.error("No valid data found in response:", response);
            // Only show toast if we're actually in edit mode
            if (this.isEdit) {
              this.$bvToast.toast("No data found for editing", {
                title: "Not Found",
                variant: "warning",
                solid: true,
              });
            }
          }
        } catch (error) {
          console.error("Error loading edit data:", error);
          // Only show error toast if we're actually in edit mode
          if (this.isEdit) {
            this.$bvToast.toast("Error loading data for editing", {
              title: this.$t("Error"),
              variant: "danger",
              solid: true,
            });
          }
        }
      },
  
      async onSourceCampusChange() {
        this.formData.sourceClassIDs = [];
        this.formData.sourceSubjectID = null;
        this.formData.sourceSessionID = null;
        this.formData.destinationSessionID = null;
        this.formData.copyToCampusIDs = [];
        this.sourceClasses = [];
        this.sourceSubjects = [];
        this.sourceSessions = [];
        this.destinationSessions = [];

        await this.loadSourceClasses();
        await this.loadSourceSubjects();
        await this.loadSourceSessions();
      },
  
      onSourceClassesChange(values) {
        if (!Array.isArray(values)) return;
        if (values.includes('ALL')) {
          const allIds = (this.sourceClasses || []).map(c => c.id);
          this.$nextTick(() => { this.formData.sourceClassIDs = allIds; });
        }
      },
  
      async onCopyToCampusesChange(values) {
        if (!Array.isArray(values)) return;

        if (!values.length) {
          this.destinationSessions = [];
          this.formData.destinationSessionID = null;
          return;
        }

        if (values.includes('ALL')) {
          const allIds = (this.campuses || [])
            .filter(c => c.id !== this.formData.sourceCampusID)
            .map(c => c.id);
          this.$nextTick(async () => {
            this.formData.copyToCampusIDs = allIds;
            await this.loadDestinationSessions();
          });
          return;
        }

        this.formData.destinationSessionID = null;
        await this.loadDestinationSessions();
      },
  
      async saveData() {
        if (!this.isFormValid) {
          this.$bvToast.toast("Please fill all required fields", {
            title: "Validation Error",
            variant: "warning",
            solid: true,
          });
          return;
        }
  
        this.saving = true;
        try {
          const payload = {
            db: this.$store.state.userData.db,
            sourceCampusID: this.formData.sourceCampusID,
            sourceClassIDs: this.formData.sourceClassIDs,
            sourceSubjectID: this.formData.sourceSubjectID,
            sourceSessionID: this.formData.sourceSessionID,
            destinationSessionID: this.formData.destinationSessionID,
            copyToCampusIDs: this.formData.copyToCampusIDs,
            userID: (this.$store.state.userData.userID || this.$store.state.userData.id || 1),
          };
  
          const obj = {
            url:
              this.$store.state.domain +
              "PlannerReplication/BulkReplication",
            body: payload,
            message: this.isEdit 
              ? "Planner replication updated successfully." 
              : "Planner replication added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
  
          const success = await this.post(obj);
          if (success) {
            this.goBack();
          }
        } catch (error) {
          console.error("Error saving data:", error);
          this.$bvToast.toast("Error saving data", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.saving = false;
        }
      },
  
      goBack() {
        this.$router.push({ name: "replicate-planner" });
      },
    },
  };
  </script>
  
  <style lang="scss" scoped>
  // Remove all custom v-select styling and use the standard approach like planner.vue
  </style>
  
  <style lang="scss">
  @import "@core/scss/vue/libs/vue-select.scss";
  </style>
  